/* Miniature of a workflow's block layout — the tile image.
   Two palettes, keyed off the product line, so the two families are
   distinguishable at a glance in the grid. */

.base {
  --preview-1: var(--accent-primary);
  --preview-2: var(--accent-secondary);
  --preview-ink: var(--text-disabled);

  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--space-3);
  gap: var(--space-2);
  background:
    radial-gradient(120% 90% at 15% 0%, var(--ai-surface) 0%, transparent 60%),
    var(--bg-surface);
  overflow: hidden;
}

.base[data-category='Streaming Metrics'] {
  --preview-1: var(--brand-teal);
  --preview-2: var(--ai-accent);
}

/* ── Window chrome: sells "this is a dashboard" in two elements ── */

.chrome {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.chromeDot {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--preview-1);
  opacity: 0.8;
  flex-shrink: 0;
}

.chromeTitle {
  height: 4px;
  width: 42%;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
}

.body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
  flex: 1;
  min-height: 0;
}

.block {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 7px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

/* Full-width blocks — the ones that read as bands in a real canvas */
.block[data-type='timeseries'],
.block[data-type='table'],
.block[data-type='note'] {
  grid-column: 1 / -1;
}

.blockLabel {
  height: 3px;
  width: 34%;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
  flex-shrink: 0;
}

/* ── KPIs ── */

.kpis {
  display: flex;
  gap: 4px;
  flex: 1;
  align-items: flex-end;
}

.kpi {
  flex: 1;
  display: flex;
  align-items: flex-end;
}

.kpiBar {
  width: 100%;
  border-radius: 1px;
  background: var(--preview-1);
}

.kpiBar[data-i='0'] { height: 12px; opacity: 0.9; }
.kpiBar[data-i='1'] { height: 8px;  opacity: 0.6; background: var(--preview-2); }
.kpiBar[data-i='2'] { height: 15px; opacity: 0.45; }

/* ── Timeseries ── */

.chart {
  flex: 1;
  min-height: 26px;
  display: flex;
}

.svg {
  width: 100%;
  height: 100%;
}

.area {
  fill: var(--preview-1);
  opacity: 0.16;
}

.line {
  fill: none;
  stroke: var(--preview-1);
  stroke-width: 1.5;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

/* ── Donut ── */

.donutRow {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-height: 0;
}

.donut {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  transform: rotate(-90deg);
}

.ring,
.arc1,
.arc2 {
  fill: none;
  stroke-width: 6;
  /* r=14 → circumference ≈ 88 */
  stroke-dasharray: 88;
}

.ring { stroke: var(--bg-active); }

.arc1 {
  stroke: var(--preview-1);
  stroke-dashoffset: 30;
}

.arc2 {
  stroke: var(--preview-2);
  stroke-dasharray: 22 88;
  stroke-dashoffset: -58;
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.legendRow {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legendDot {
  width: 4px;
  height: 4px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
}

.legendDot[data-i='0'] { background: var(--preview-1); }
.legendDot[data-i='1'] { background: var(--preview-2); }
.legendDot[data-i='2'] { background: var(--preview-ink); }

.legendBar {
  height: 3px;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
}

.legendBar[data-i='0'] { width: 72%; }
.legendBar[data-i='1'] { width: 54%; }
.legendBar[data-i='2'] { width: 38%; }

/* ── Split ── */

.split {
  display: flex;
  gap: 3px;
  flex: 1;
  align-items: center;
}

.splitLeft,
.splitRight {
  height: 9px;
  border-radius: 2px;
}

.splitLeft {
  width: 62%;
  background: var(--preview-1);
  opacity: 0.85;
}

.splitRight {
  width: 38%;
  background: var(--preview-2);
  opacity: 0.55;
}

/* ── Table ── */

.table {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  justify-content: center;
}

.tableRow {
  height: 3px;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
}

.tableRow[data-i='0'] { width: 100%; background: var(--preview-1); opacity: 0.7; }
.tableRow[data-i='1'] { width: 88%; }
.tableRow[data-i='2'] { width: 94%; }
.tableRow[data-i='3'] { width: 76%; }

/* ── Note ── */

.note {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  justify-content: center;
}

.noteLine {
  height: 3px;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
}

.noteLine[data-i='0'] { width: 100%; }
.noteLine[data-i='1'] { width: 64%; }
